/**
 * Shared language for file ingestion across Louise.
 *
 * This is deliberately domain-neutral. A document, lab result, insurance
 * card, or any future upload surface can use the same lifecycle while its
 * review content remains surface-specific.
 */

export const INGESTION_STATES = [
  "idle",
  "selecting",
  "validating",
  "queued",
  "uploading",
  "uploaded",
  "processing",
  "background_processing",
  "review_ready",
  "needs_review",
  "saving",
  "complete",
  "source_only",
  "partial_success",
  "failed_retryable",
  "unsupported",
  "rejected",
  "canceled",
] as const;

export type IngestionState = (typeof INGESTION_STATES)[number];

/** Whether processing remains in the current surface or is handed off. */
export type IngestionContinuation = "foreground" | "background";

export type IngestionUserAction =
  | "choose_file"
  | "cancel"
  | "retry"
  | "review"
  | "dismiss"
  | "none";

export type IngestionStatePresentation = {
  label: string;
  description: string;
  action: IngestionUserAction;
};

/**
 * User-facing language belongs beside the state contract so each surface
 * does not invent a different meaning for the same lifecycle state.
 * Product translations can map these English source strings to locale keys.
 */
export const INGESTION_STATE_PRESENTATION: Readonly<
  Record<IngestionState, IngestionStatePresentation>
> = {
  idle: {
    label: "Ready to upload",
    description: "Choose a file to begin.",
    action: "choose_file",
  },
  selecting: {
    label: "Selecting file",
    description: "Choose the file you want to add.",
    action: "cancel",
  },
  validating: {
    label: "Checking file",
    description: "We are checking the file type and size.",
    action: "cancel",
  },
  queued: {
    label: "Waiting to upload",
    description: "Your file is queued and will start uploading shortly.",
    action: "cancel",
  },
  uploading: {
    label: "Uploading",
    description: "Keep this window open while we securely upload your file.",
    action: "cancel",
  },
  uploaded: {
    label: "Upload complete",
    description: "Your file is secure. We are preparing it now.",
    action: "none",
  },
  processing: {
    label: "Processing",
    description:
      "We are reading your file and preparing information to review.",
    action: "none",
  },
  background_processing: {
    label: "Processing in the background",
    description:
      "You can keep using Louise. We will let you know when it is ready.",
    action: "dismiss",
  },
  review_ready: {
    label: "Ready to review",
    description: "We found information that is ready for you to check.",
    action: "review",
  },
  needs_review: {
    label: "Review needed",
    description: "Check the details before saving them to your records.",
    action: "review",
  },
  saving: {
    label: "Saving",
    description: "We are adding the information to your records.",
    action: "none",
  },
  complete: {
    label: "Saved",
    description: "Your information has been added to your records.",
    action: "dismiss",
  },
  source_only: {
    label: "File saved",
    description:
      "We saved the original file, but could not extract structured information.",
    action: "dismiss",
  },
  partial_success: {
    label: "Partially saved",
    description:
      "Some information was saved. Review the items that need attention.",
    action: "review",
  },
  failed_retryable: {
    label: "Upload paused",
    description:
      "Something went wrong. You can try again from the last safe step.",
    action: "retry",
  },
  unsupported: {
    label: "File not supported",
    description: "Choose a different file type or format.",
    action: "choose_file",
  },
  rejected: {
    label: "Could not accept file",
    description:
      "This file cannot be added. Choose a different file or check its contents.",
    action: "choose_file",
  },
  canceled: {
    label: "Canceled",
    description: "This upload was canceled before it was saved.",
    action: "dismiss",
  },
};

export const INGESTION_TERMINAL_STATES: readonly IngestionState[] = [
  "complete",
  "source_only",
  "partial_success",
  "unsupported",
  "rejected",
  "canceled",
];

export const INGESTION_ACTIVE_STATES: readonly IngestionState[] = [
  "selecting",
  "validating",
  "queued",
  "uploading",
  "uploaded",
  "processing",
  "background_processing",
  "saving",
];

export const INGESTION_ATTENTION_STATES: readonly IngestionState[] = [
  "review_ready",
  "needs_review",
  "partial_success",
  "failed_retryable",
  "unsupported",
  "rejected",
];
